import React, { useEffect, useState } from "react";
import api from "../../../api/axios";
import Swal from "sweetalert2";

export default function LoanManagement() {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usingSample, setUsingSample] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    employee_id: "",
    start_date: "",
    principal: "",
    tenure: "",
    interest_rate: "",
    notes: "",
  });

  useEffect(() => {
    loadLoans();
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      const res = await api.get("hrms/employees");
      setEmployees(res.data.data);
    } catch (err) {
      console.warn("Employee API unavailable → using sample employees");

      setEmployees([
        { id: 1, first_name: "Alice", last_name: "Johnson" },
        { id: 2, first_name: "Bob", last_name: "Williams" },
        { id: 3, first_name: "Charlie", last_name: "Brown" },
        { id: 4, first_name: "Diana", last_name: "Miller" },
      ]);
    }
  };

  const loadLoans = async () => {
    setLoading(true);

    try {
      const res = await api.get("hrms/prms/loans");
      setLoans(res.data.data.data);
      setUsingSample(false);
    } catch (err) {
      console.warn("API not ready → using sample loans");

      const sampleLoans = [
        {
          id: 1,
          employee: { first_name: "Alice", last_name: "Johnson" },
          start_date: "2023-01-15",
          status: "Active",
          principal: "50000",
          interest_rate: "5%",
        },
        {
          id: 2,
          employee: { first_name: "Bob", last_name: "Williams" },
          start_date: "2023-03-20",
          status: "Active",
          principal: "20000",
          interest_rate: "7%",
        },
        {
          id: 3,
          employee: { first_name: "Charlie", last_name: "Brown" },
          start_date: "2022-11-01",
          status: "Paid Off",
          principal: "100000",
          interest_rate: "4%",
        },
        {
          id: 4,
          employee: { first_name: "Diana", last_name: "Miller" },
          start_date: "2023-08-10",
          status: "Pending",
          principal: "35000",
          interest_rate: "5.5%",
        },
      ];

      setLoans(sampleLoans);
      setUsingSample(true);
    }

    setLoading(false);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const saveLoan = async () => {
    setSaving(true);

    try {
      await api.post("hrms/prms/loans", form);

      Swal.fire({
        title: "Success!",
        text: "Loan added successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      setShowForm(false);
      setForm({
        employee_id: "",
        start_date: "",
        principal: "",
        tenure: "",
        interest_rate: "",
        notes: "",
      });

      await loadLoans();
    } catch (err) {
      Swal.fire({
        title: "Sample Mode",
        text: "Loan saved locally (API not ready).",
        icon: "warning",
      });

      setLoans([
        ...loans,
        {
          id: loans.length + 1,
          employee: employees.find((e) => e.id == form.employee_id),
          start_date: form.start_date,
          tenure: form.tenure,
          notes: form.notes,
          status: "Pending",
          principal: form.principal,
          interest_rate: form.interest_rate,
        },
      ]);

      setShowForm(false);
    }

    setSaving(false);
  };

  if (loading) {
    return <div className="text-center p-5 fw-bold">Loading loans...</div>;
  }

  return (
    <div style={{ background: "#f3f4f5ff", minHeight: "100vh" }}>
      <div className="container py-4">
        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="fw-bold">Loan Management</h3>

          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            <i className="bi bi-plus-circle me-2"></i>Add Loan
          </button>
        </div>

        {usingSample && (
          <span className="badge bg-warning text-dark mb-3">
            Using Sample Data
          </span>
        )}

        {/* ADD LOAN FORM */}
        {showForm && (
          <div className="bg-white border rounded shadow-sm p-4 mb-4">
            <h5 className="fw-bold mb-3">Add Loan</h5>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Employee</label>
                <select
                  name="employee_id"
                  className="form-select"
                  value={form.employee_id}
                  onChange={handleChange}
                >
                  <option value="">Select Employee</option>

                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">Start Date</label>
                <input
                  type="date"
                  name="start_date"
                  className="form-control"
                  value={form.start_date}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">Amount (INR)</label>
                <input
                  type="number"
                  name="principal"
                  className="form-control"
                  value={form.principal}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Interest Rate (%)
                </label>
                <input
                  type="number"
                  name="interest_rate"
                  className="form-control"
                  value={form.interest_rate}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Tenure (Months)
                </label>
                <input
                  type="number"
                  name="tenure"
                  className="form-control"
                  value={form.tenure}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">Notes</label>
                <input
                  type="text"
                  name="notes"
                  className="form-control"
                  value={form.notes}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="mt-4 d-flex justify-content-end gap-2">
              <button
                className="btn btn-secondary"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                onClick={saveLoan}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Saving...
                  </>
                ) : (
                  "Save Loan"
                )}
              </button>
            </div>
          </div>
        )}

        {/* LOAN TABLE */}
        <div className="bg-white border rounded shadow-sm p-4">
          <h5 className="fw-bold mb-4">Employee Loans</h5>

          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Start Date</th>
                  <th>Amount</th>
                  <th>Interest Rate</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {loans.map((loan) => (
                  <tr key={loan.id}>
                    <td>
                      {loan.employee?.first_name} {loan.employee?.last_name}
                    </td>
                    <td>
                      {new Date(loan.start_date).toISOString().split("T")[0]}
                    </td>
                    <td>{loan.principal}</td>
                    <td>{loan.interest_rate}</td>
                    <td>
                      <LoanStatus status={loan.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Status Component */
function LoanStatus({ status }) {
  if (status?.toLowerCase() === "active")
    return (
      <span className="badge rounded-pill bg-success-subtle text-success px-3 py-2">
        Active
      </span>
    );

  if (status?.toLowerCase() === "paid off")
    return (
      <span className="badge rounded-pill bg-secondary-subtle text-secondary px-3 py-2">
        Paid Off
      </span>
    );

  if (status?.toLowerCase() === "pending")
    return (
      <span className="badge rounded-pill bg-warning-subtle text-warning px-3 py-2">
        Pending
      </span>
    );

  return status;
}
